import { datadogRum } from '@datadog/browser-rum';
import { kebabCase } from 'lodash';
import type { saveTypeEnum } from '@common/enums';
import { scrollValidationErrorIntoCenterViewInScrollContainer } from '@frontend/utils';
import { asyncDebounce } from '@frontend/utils/asyncDebounce';
import { startView } from '@frontend/utils/hooks/useDatadogView';
import { prepareDiagnosisForSave } from '@frontend/utils/prepareDiagnosisForSave';
import type { ReportCategories } from '../../../../common/enums/reportCategories';
import type { ValidationErrors } from '../../../utils/validation';
import type { IAsset } from '../../interfaces/asset';
import type { IDiagnosis } from '../../interfaces/diagnosis';
import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IProcess } from '../../interfaces/process';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { IServiceGetOne } from '../../interfaces/serviceGetOne';
import type { IItemState } from '../episodes/Episode';
import type { IEpisodeContext } from '../episodes/EpisodeContext';
import type { ReloadEpisode } from '../episodes/reloadEpisode';
import { CustomControlEntity } from '../../../../common/enums/customControlEntity';
import { nullDiagnosis } from '../../../../common/validation/diagnosis';
import {
    getDiagnosticReportValidationSchema,
    getSaveAndSaveAndSendValidationSchema,
} from '../../../../common/validation/diagnosticReport';
import { getDiagnosticReportValidationType } from '../../../../common/validation/getDiagnosticReportValidationType';
import { fetchJson } from '../../../store/fetch';
import { useEpisodeContext } from '../../../utils/hooks/useEpisodeContext';
import { isAutoSaveActiveByService } from '../../../utils/isAutoSaveActiveByService';
import {
    emptyValidationErrors,
    updateValidationErrorsForOne,
    validate,
    validationErrorsToString,
} from '../../../utils/validation';
import { Component } from '../../elements/Component';
import { initializeCustomControl } from '../customControls/initializeCustomControl';
import { loadCustomControl } from '../customControls/loadCustomControl';
import { validateCustomControlData } from '../customControls/validateCustomControlData';
import { validateDiagnosis } from '../customControls/validateDiagnosis';
import { validateOneCustomControlData } from '../customControls/validateOneCustomControlData';

interface IDiagnosticReportItemAddEditProps {
    category?: ReportCategories;
    diagnosticReport?: IDiagnosticReportGetOne;
    disableFormAutosave: boolean;
    formId: string;
    isFirstItem: boolean;
    isLastItem: boolean;
    itemState: IItemState;
    onCancel: (requestId: string | undefined) => Promise<void>;
    onSave: (diagnosticReportId: string) => Promise<void>;
    process: IProcess;
    reloadProcess: (mode: ReloadEpisode) => Promise<void>;
    removeDiagnosticReport?: () => Promise<void>;
    request: IRequestGetOne;
    setHideCreateActions: (value: boolean) => Promise<void>;
}

export interface IDiagnosticReportAddEdit {
    assets: IAsset[];
    category: ReportCategories;
    customControlData: any;
    diagnosis: IDiagnosis;
    diagnosisRemarks: string | null;
    id?: string;
    patientId: string;
    remark: string | null;
    requestId?: string;
}

interface IDiagnosticReportItemAddEditState {
    canAutoSave: boolean;
    diagnosticReport?: IDiagnosticReportAddEdit;
    errors: ValidationErrors;
    isFirstTimeSaving: boolean;
    saveType: 'final' | 'draft';
    service?: IServiceGetOne;
}

export function DiagnosticReportItemAddEdit(props: IDiagnosticReportItemAddEditProps) {
    const episodeContext = useEpisodeContext();

    return (
        <DiagnosticReportItemAddEditInner
            {...props}
            {...episodeContext}
        />
    );
}

export class DiagnosticReportItemAddEditInner extends Component<
    IDiagnosticReportItemAddEditProps & IEpisodeContext,
    IDiagnosticReportItemAddEditState
> {
    private debouncedSave = asyncDebounce(this.saveDiagnosticReport.bind(this), 300);

    constructor(props: IDiagnosticReportItemAddEditProps & IEpisodeContext, context?: {}) {
        super(props, context);

        this.state = {
            errors: emptyValidationErrors,
            saveType: 'draft',
            isFirstTimeSaving: !this.props.diagnosticReport?.id,
            canAutoSave: false,
        };
    }

    public async componentDidMount() {
        await this.load();
        await this.setState({ canAutoSave: true });

        if (this.state.service?.episodeCategory) {
            startView(
                `/episode/${this.state.service.episodeCategory.name}/${this.state.service.code.toLowerCase()}_Report`
            );
        }
    }

    public componentWillUnmount() {
        this.props.setAutoSaveStatus('idle');
    }

    public async componentDidUpdate(
        prevProps: Readonly<IDiagnosticReportItemAddEditProps & IEpisodeContext>,
        prevState: Readonly<IDiagnosticReportItemAddEditState>
    ) {
        if (
            prevState.isFirstTimeSaving !== this.state.isFirstTimeSaving ||
            prevState.diagnosticReport?.id !== this.state.diagnosticReport?.id
        ) {
            await this.setState({ canAutoSave: this.state.isFirstTimeSaving || !!this.state.diagnosticReport?.id });
        }
    }

    public render() {
        if (!this.state.service || !this.state.diagnosticReport) {
            return null;
        }
        const CustomControl = loadCustomControl(
            this.props.request.customControlVersion,
            CustomControlEntity.DiagnosticReportAddEdit,
            this.props.request.service.code,
            this.props.request.service.serviceType,
            this.state.diagnosticReport.category
        );

        return (
            <CustomControl
                currentDiagnosticReport={this.props.diagnosticReport}
                diagnosticReport={this.state.diagnosticReport}
                errors={this.state.errors}
                formId={this.props.formId}
                handleChange={async (name, value) => await this.handleChange(name, value)}
                handleChangeCustomControl={async (values) => await this.handleChangeCustomControl(values)}
                isAutoSaveActive={isAutoSaveActiveByService(
                    this.props.request.service.code,
                    this.props.disableFormAutosave
                )}
                isFirstItem={this.props.isFirstItem}
                itemState={this.props.itemState}
                onCancel={() => this.props.onCancel(this.state.diagnosticReport && this.state.diagnosticReport.id)}
                onSave={this.props.onSave}
                onSaveDiagnosticReport={async (diagnosticReport) => {
                    await this.setState({ canAutoSave: false });

                    return await this.debouncedSave(diagnosticReport);
                }}
                process={this.props.process}
                reloadProcess={this.props.reloadProcess}
                removeDiagnosticReport={this.props.removeDiagnosticReport}
                request={this.props.request}
                revertDiagnosticReportChanges={this.revertDiagnosticReportChanges.bind(this)}
                service={this.state.service}
                setHideCreateActions={this.props.setHideCreateActions}
                validateDiagnosticReport={async (saveType) => await this.validateDiagnosticReport(saveType)}
                validateOne={async (name) => await this.validateOne(name)}
                validateOneCustomControl={async (name) => await this.validateOneCustomControl(name)}
            />
        );
    }

    private async handleChange<K extends keyof IDiagnosticReportAddEdit>(name: K, value: IDiagnosticReportAddEdit[K]) {
        await this.setState(({ diagnosticReport }) => ({ diagnosticReport: { ...diagnosticReport, [name]: value } }));
        if (name === 'assets') {
            await this.autoSaveDiagnosticReport(this.state.errors);
        }
    }

    private async validateOne(name: string) {
        const validationType = this.getDiagnosticReportValidationType('final');

        if (name === 'diagnosis') {
            await this.setState(({ diagnosticReport, errors }) => {
                if (diagnosticReport) {
                    errors = validateDiagnosis(
                        this.props.request.customControlVersion,
                        CustomControlEntity.DiagnosticReportAddEdit,
                        this.props.request.service.code,
                        diagnosticReport.diagnosis,
                        errors,
                        diagnosticReport.customControlData
                    );

                    return { errors };
                }
            });
        }

        const updatedErrors = updateValidationErrorsForOne(
            getDiagnosticReportValidationSchema(validationType),
            this.state.diagnosticReport,
            this.state.errors,
            name
        );

        if (!updatedErrors.isEmpty()) {
            this.props.setAutoSaveStatus('idle');
        }

        await this.setState({ errors: updatedErrors });

        if (await this.validateDiagnosticReport('draft')) {
            await this.autoSaveDiagnosticReport(updatedErrors);
        }
    }

    private async handleChangeCustomControl(values: any) {
        await this.setState(({ diagnosticReport }) => ({
            diagnosticReport: {
                ...diagnosticReport,
                customControlData: { ...diagnosticReport!.customControlData, ...values },
            },
        }));
    }

    private async validateOneCustomControl(name: string) {
        const customControlName = 'customControlData.' + name;
        const validationType = this.getDiagnosticReportValidationType('final');

        let errors = updateValidationErrorsForOne(
            getDiagnosticReportValidationSchema(validationType),
            this.state.diagnosticReport,
            this.state.errors,
            customControlName
        );
        errors = validateOneCustomControlData(
            this.props.process.customControlVersion,
            CustomControlEntity.DiagnosticReportAddEdit,
            this.state.service!.code,
            this.state.diagnosticReport!.customControlData,
            name,
            errors,
            this.props.process,
            undefined,
            undefined,
            this.state.diagnosticReport?.diagnosis
        );
        await this.setState({ errors });

        if (await this.validateDiagnosticReport('draft')) {
            await this.autoSaveDiagnosticReport(errors);
        }
    }

    private async loadService() {
        const url = `/api/services/${this.props.request.serviceId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ service: result.json });
    }

    private getDiagnosticReportValidationType(draftOrFinal: 'draft' | 'final') {
        if (!this.state.diagnosticReport) {
            throw new Error('diagnosticReport undefined');
        }

        return getDiagnosticReportValidationType(
            this.props.request.service.code,
            this.props.request.service.serviceType,
            this.state.diagnosticReport.category,
            this.props.request.customControlVersion,
            (this.props.diagnosticReport && this.props.diagnosticReport.id) || this.state.diagnosticReport.id
                ? 'edit'
                : 'add',
            draftOrFinal
        );
    }

    private async load() {
        await this.loadService();
        if (this.props.diagnosticReport) {
            await this.setState({
                diagnosticReport: initializeFromDiagnosticReport(this.props.diagnosticReport),
            });
        } else {
            if (!this.props.category) {
                throw new Error('Diagnostic Report category not set');
            }
            const customControlData = initializeCustomControl(
                this.props.request.customControlVersion,
                CustomControlEntity.DiagnosticReportAddEdit,
                this.props.request.service.code,
                this.props.request.service.serviceType,
                undefined,
                this.props.category
            );
            await this.setState({
                diagnosticReport: initializeDiagnosticReport(
                    this.props.patient.id,
                    this.props.category,
                    this.props.request.id,
                    customControlData
                ),
            });
        }
    }

    private async validateDiagnosticReport(saveType: 'final' | 'draft'): Promise<boolean> {
        if (!this.state.diagnosticReport) {
            return false;
        }

        const validationType = this.getDiagnosticReportValidationType(saveType);
        const validationResult = validate(
            getDiagnosticReportValidationSchema(validationType),
            this.state.diagnosticReport
        );

        let errors = validationResult.errors;
        if (saveType === 'final') {
            errors = validateCustomControlData(
                this.props.request.customControlVersion,
                CustomControlEntity.DiagnosticReportAddEdit,
                this.props.request.service.code,
                this.state.diagnosticReport.customControlData,
                errors,
                this.props.process,
                undefined,
                undefined,
                this.state.diagnosticReport.diagnosis
            );
            errors = validateDiagnosis(
                this.props.request.customControlVersion,
                CustomControlEntity.DiagnosticReportAddEdit,
                this.props.request.service.code,
                this.state.diagnosticReport.diagnosis,
                errors,
                this.state.diagnosticReport.customControlData
            );
        }
        await this.setState({ errors, saveType });
        if (errors.size > 0) {
            scrollValidationErrorIntoCenterViewInScrollContainer(this.props.formId, errors);

            return false;
        }

        return true;
    }

    private async saveDiagnosticReport(saveType: 'draft' | 'final') {
        this.props.setAutoSaveStatus('saving');

        if (!this.state.diagnosticReport) {
            return;
        }

        await this.setState({ isFirstTimeSaving: false });

        let url = '/api/diagnosticReports';
        url += saveType === 'final' ? '/saveAndSend' : '/save';

        const validationType = this.getDiagnosticReportValidationType(saveType);
        const validatedDiagnosticReport = validate(getSaveAndSaveAndSendValidationSchema(validationType), {
            assets: this.state.diagnosticReport.assets,
            diagnosticReport: this.state.diagnosticReport,
            diagnosis: prepareDiagnosisForSave(this.state.diagnosticReport.diagnosis, saveType as saveTypeEnum),
            validationType,
        });

        if (validatedDiagnosticReport.errors.size > 0) {
            if (
                isAutoSaveActiveByService(this.props.request.service.code, this.props.disableFormAutosave) &&
                saveType === 'draft'
            ) {
                return;
            } else {
                throw new Error(
                    `Errors in save diagnosticReport validation:\n${validationErrorsToString(
                        validatedDiagnosticReport.errors
                    )}`
                );
            }
        }

        const result = await fetchJson(url, 'POST', validatedDiagnosticReport.item);

        if (saveType === 'final') {
            datadogRum.addAction(`Save ${kebabCase(this.state.service?.code.toLowerCase() + '_Report')} complete`);
        }

        const diagnosticReportResult = await fetchJson(
            `/api/diagnosticReports/${result.json.id}?showAssets=true`,
            'GET'
        );

        await this.setState(({ diagnosticReport }: IDiagnosticReportItemAddEditState) => ({
            diagnosticReport: { ...diagnosticReport, id: result.json.id, assets: diagnosticReportResult.json.assets },
        }));

        this.props.setAutoSaveStatus('saved');

        return result.json.id;
    }

    private async autoSaveDiagnosticReport(errors: ValidationErrors) {
        if (
            errors.size === 0 &&
            isAutoSaveActiveByService(
                this.state.service!.code,
                this.props.disableFormAutosave,

                this.props.process.customControlVersion,
                this.state.diagnosticReport!.customControlData
            ) &&
            this.state.canAutoSave
        ) {
            await this.debouncedSave('draft');
        }
    }

    private async revertDiagnosticReportChanges() {
        if (this.props.diagnosticReport) {
            const originalDiagnosticReport: Partial<IDiagnosticReportAddEdit> | undefined = {
                ...this.state.diagnosticReport,
                ...this.props.diagnosticReport,
            };

            await this.setState({ diagnosticReport: originalDiagnosticReport as IDiagnosticReportAddEdit });
            await this.debouncedSave('draft');
        } else {
            if (this.state.diagnosticReport?.id) {
                const url = `/api/diagnosticReports/${this.state.diagnosticReport.id}`;
                await fetchJson(url, 'DELETE');
            }
            await this.props.removeDiagnosticReport?.();
        }
    }
}

export function initializeDiagnosticReport(
    patientId: string,
    category: ReportCategories,
    requestId: string | undefined,
    customControlData: any
) {
    const diagnosticReport: IDiagnosticReportAddEdit = {
        patientId: patientId,
        requestId: requestId,
        remark: null,
        category: category,
        customControlData: customControlData || null,
        diagnosis: { ...nullDiagnosis },
        diagnosisRemarks: null,
        assets: [],
    };

    return diagnosticReport;
}
export function initializeFromDiagnosticReport(diagnosticReport: IDiagnosticReportGetOne) {
    const result: IDiagnosticReportAddEdit = {
        id: diagnosticReport.id,
        patientId: diagnosticReport.patientId,
        requestId: diagnosticReport.requestId,
        remark: diagnosticReport.remark || null,
        category: diagnosticReport.category,
        customControlData: diagnosticReport.customControlData || null,
        diagnosis: diagnosticReport.diagnosis || { ...nullDiagnosis },
        diagnosisRemarks: diagnosticReport.diagnosisRemarks || null,
        assets: diagnosticReport.assets ? diagnosticReport.assets : [],
    };

    return result;
}
